import {
  attachMraidMarkers,
  checkMraid,
  sendMraidDebugTracks
} from 'common/js/TrackAnalyticsHelper';
import { openBrowser } from 'common/js/browser-utils';
import { isFeatureEnable } from 'common/js/features-enabling';
import {
  getMraidVersion,
  listenCallTagEvent,
  triggerOnAdLoaded as onAdLoaded,
  unloadFormatOnReady
} from 'common/js/mraid-utils';
import { hashString } from 'common/js/utils';
import { makeAutoObservable, runInAction } from 'mobx';
import { UITimerStore } from './UITimerStore';
import { AnalyticsStore } from './AnalyticsStore';
import {
  AD_DISPLAYED,
  RENDERING_ERROR,
  UNLOAD_ERROR
} from '../../@types/supply-monitoring-events.type';
import { DisplayConfigType } from 'formats/display/lib/config';

export interface MraidStoreProps {
  uiTimerStore: UITimerStore;
  formatOptions: FormatOptions;
  formatName: FormatName;
  formatConfig?: DisplayConfigType;
  onOpenCalled?: (mraidOpen: any, url: string) => void;
  onOpenNoRedirectCalled?: (mraidOpen: any, url: string) => void;
  onCloseCalled?: (mraidClose: any) => void;
  onUnloadCalled?: (originalUnload: () => void) => void;
  onViewable?: () => void;
  onReady?: () => void;
}

let exposureTrackNumber: number = 0;

export class MraidStore {
  canDisplayCloseButton: boolean = true;
  isReady: boolean = false;
  isViewable: boolean = false;
  exposure: number = 0;
  hasBeenViewable: boolean = false;
  isExpanded: boolean = false;
  isResized: boolean = false;

  mraidState: MRAIDState = 'loading';
  uiTimerStore: UITimerStore;
  formatOptions: FormatOptions;
  formatName: FormatName;
  formatConfig?: DisplayConfigType;
  onOpenCalled?: (mraidOpen: any, url: string) => void;
  onOpenNoRedirectCalled?: (mraidOpen: any, url: string) => void;
  onCloseCalled?: (mraidClose: any) => void;
  onUnloadCalled?: (unload: any) => void;
  onViewable?: () => void;
  onReady?: () => void;

  constructor({
    uiTimerStore,
    formatOptions,
    formatName,
    formatConfig,
    onOpenCalled,
    onOpenNoRedirectCalled,
    onCloseCalled,
    onUnloadCalled,
    onViewable,
    onReady
  }: MraidStoreProps) {
    attachMraidMarkers();

    this.uiTimerStore = uiTimerStore;
    this.formatOptions = formatOptions;
    this.formatName = formatName;
    this.formatConfig = formatConfig;
    this.onOpenCalled = onOpenCalled;
    this.onOpenNoRedirectCalled = onOpenNoRedirectCalled;
    this.onCloseCalled = onCloseCalled;
    this.onUnloadCalled = onUnloadCalled;
    this.onViewable = onViewable;
    this.onReady = onReady;

    this.overloadMraid();
    this.listenViewabilityChange();
    this.listenMraidReady();
    this.listenStateChange();
    makeAutoObservable(this);
  }

  listenViewabilityChange() {
    checkMraid('listenViewabilityChange');

    if ((window.mraid as any).__ogyPolyfill) {
      sendMraidDebugTracks('format_mraid_polyfilled');
    }

    sendMraidDebugTracks('format_mraid_version_' + getMraidVersion());

    sendMraidDebugTracks('format_mraid_viewable');

    this.onExposureChange(this.isMraidViewable() ? 100 : 0);

    const listener = (isViewable: boolean) => {
      checkMraid('viewableChange');

      if (isViewable) {
        sendMraidDebugTracks('format_mraid_viewable');
        this.onExposureChange(100);
      } else {
        this.onExposureChange(this.isMraidViewable() ? 100 : 0);
      }
    };

    // Override toString method to fix Mopub addEventListerner implementation
    // Mopub use String(listener) to compare if the listener function exists or not
    // We hash the callback function to ensure that this mecanism is still working but
    // we only consider that the callback function must be different
    listener.toString = () => `fct${hashString(String(this.onViewableChange))}`;
    window.mraid.addEventListener('viewableChange', listener);
    if (getMraidVersion() >= 3) {
      window.mraid.addEventListener(
        'exposureChange',
        (exposedPercentage: number) => {
          checkMraid('exposureChange');

          if (exposureTrackNumber < 10) {
            if (
              isNaN(exposedPercentage) ||
              exposedPercentage === null ||
              exposedPercentage === undefined
            ) {
              sendMraidDebugTracks(`format_mraid_exposure_not_a_number`);
            } else if (exposedPercentage < 10) {
              sendMraidDebugTracks(`format_mraid_exposure_below_10`);
            } else if (exposedPercentage < 25) {
              sendMraidDebugTracks(`format_mraid_exposure_below_25`);
            } else if (exposedPercentage < 50) {
              sendMraidDebugTracks(`format_mraid_exposure_below_50`);
            }

            exposureTrackNumber++;
          }

          this.onExposureChange(exposedPercentage);
        }
      );
    }
  }

  onExposureChange(exposure: number) {
    this.exposure = exposure;
    this.onViewableChange(exposure >= 50);
  }

  onViewableChange(isViewable: boolean) {
    if (this.isViewable === isViewable) return;

    this.isViewable = isViewable;
    sendMraidDebugTracks(`format_mraid_viewable_change_${isViewable}`);

    if (!isViewable) {
      this.uiTimerStore.pause();
    } else {
      this.hasBeenViewable = true;
      this.onViewable && this.onViewable();
      this.uiTimerStore.resume();
    }
  }

  get mraidViewability() {
    return this.isViewable;
  }

  get mraidHasBeenViewable() {
    return this.hasBeenViewable;
  }

  listenMraidReady() {
    checkMraid('listenMraidReady');

    const listener = () => {
      checkMraid('ready');
      sendMraidDebugTracks('format_mraid_ready');
      this.overrideUseCustomClose();
      this.isReady = true;
      this.onReady && this.onReady();
      this.listenCallTag();
    };
    if (window.mraid.getState() !== 'loading') {
      listener();
    }
    window.mraid.addEventListener('ready', listener);
  }

  listenStateChange() {
    checkMraid('listenStateChange');
    const listener = () => {
      checkMraid('stateChange');
      sendMraidDebugTracks(`format_state_change_${window.mraid.getState()}`);
      this.mraidState = window.mraid.getState();
      this.isResized = window.mraid.getState() === 'resized';
    };
    window.mraid.addEventListener('stateChange', listener);
  }

  get isDefaultMinimized(): boolean {
    return !!window?.OG_AD_UNIT_CONFIG?.params?.minimized_by_default;
  }

  get isAdBelowHeader(): boolean {
    return (
      window?.OG_AD_UNIT_CONFIG?.params?.header_ad_dom_position ===
      'BELOW_HEADER'
    );
  }

  get isAdAboveHeader(): boolean {
    return (
      window?.OG_AD_UNIT_CONFIG?.params?.header_ad_dom_position ===
      'ABOVE_HEADER'
    );
  }

  get currentMraidState() {
    return this.mraidState;
  }

  get isMraidReady() {
    return this.isReady;
  }

  get isMraidExpanded() {
    return this.isExpanded;
  }

  get isMraidResized() {
    return this.isResized;
  }

  close() {
    window.mraid.close();
  }

  forceClose() {
    // Prefer ogyForceClose (bypasses clearCloseCallbackAfterClick no-op override).
    // Fallback to close() for external SDKs where ogyForceClose is not available.
    if (window.mraid.ogyForceClose) {
      window.mraid.ogyForceClose();
    } else {
      window.mraid.close();
    }
  }

  expand() {
    checkMraid('expand');
    this.isExpanded = true;
    window.mraid.expand();
  }

  collapse() {
    checkMraid('collapse');
    this.isExpanded = false;
    window.mraid.close();
  }

  setResizeProperties(properties: MRAIDResizeProperties): void {
    checkMraid('setResizeProperties');
    if (typeof window.mraid.setResizeProperties === 'function') {
      window.mraid.setResizeProperties(properties);
    }
  }

  resize(): void {
    checkMraid('resize');
    if (typeof window.mraid.resize === 'function') {
      window.mraid.resize();
    }
  }

  overloadMraid() {
    checkMraid('overloadMraid');

    this.overrideOpen();
    this.overrideClose();
    this.overrideUnload();
  }

  manageMraidOpen(mraidOpen: (url: string) => void, url: string): void {
    if (isFeatureEnable('oguryBrowser')) {
      openBrowser(this.formatOptions, url);
    } else {
      mraidOpen(url);
    }
  }

  triggerOnAdImpression() {
    checkMraid('triggerOnAdImpression');
    sendMraidDebugTracks(`format_trigger_on_ad_impression`);
    window.mraid.ogyOnAdImpression?.();
    AnalyticsStore.instance?.sendSupplyMonitoringEvent(AD_DISPLAYED);
  }

  triggerOnAdLoaded() {
    checkMraid('triggerOnAdLoaded');
    sendMraidDebugTracks(`format_trigger_on_ad_loaded`);
    onAdLoaded();

    // loaded track is usually sent by the wrapper, but if the format is not Mraid compliant, we need to send it ourselves
    if (!this.formatConfig?.isMraidCompliant) {
      AnalyticsStore.instance?.sendAdsEventsTrack('loaded');
    }
  }

  triggerUnload() {
    unloadFormatOnReady();
    AnalyticsStore.instance?.sendSupplyMonitoringEvent(RENDERING_ERROR, {
      error_type: UNLOAD_ERROR
    });
  }

  listenCallTag() {
    window.addEventListener('calltag', listenCallTagEvent);
  }

  overrideUseCustomClose() {
    const canEdit =
      !Object.isFrozen(window.mraid) && !Object.isSealed(window.mraid);
    if (this.formatOptions?.context?.creativeSource !== 'redirect') {
      return;
    }

    try {
      const formerMethod = window.mraid.useCustomClose.bind(window.mraid);

      formerMethod(true);

      if (canEdit) {
        window.mraid.useCustomClose = (tagWantsHisOwn: boolean) => {
          runInAction(() => {
            this.canDisplayCloseButton = !tagWantsHisOwn;
          });
        };
      }
    } catch (e) {
      console.error(e);
    }
  }

  overrideOpen() {
    (open => {
      open = open && open.bind(window.mraid);
      window.mraid.open = url => {
        if (this.onOpenCalled) {
          this.onOpenCalled(open, url);
        } else if (open) {
          this.manageMraidOpen(open, url);
        }
      };

      window.mraid.openNoRedirect = url => {
        if (this.onOpenNoRedirectCalled) {
          this.onOpenNoRedirectCalled(open, url);
        } else if (open) {
          open(url);
        }
      };
      if (open && !window.mraidOriginalOpen) {
        window.mraidOriginalOpen = open;
      }
    })(window.mraid.open as MraidOpen);
  }

  overrideClose() {
    (close => {
      close = close && close.bind(window.mraid);
      window.mraid.close = (...args) => {
        if (this.onCloseCalled) {
          this.onCloseCalled(close);
        } else {
          close(...args);
        }
      };
    })(window.mraid.close);
  }

  overrideUnload() {
    if (!this.onUnloadCalled) return;

    (unload => {
      unload = unload && unload.bind(window.mraid);
      window.mraid.unload = this.onUnloadCalled.bind(this, unload);
    })(window.mraid.unload);
  }

  isMraidViewable(): boolean {
    const isViewable = window.mraid.isViewable();
    return isViewable === true || isViewable === 'true';
  }
}
